import {
  Button,
  UpdateIconButton,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  formatCurrency,
  formatDate,
  formatPercent,
  pn,
  Typography,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  ContributorEmploymentListDto,
  ContributorEmploymentListFilter,
  ContributorEmploymentListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import "moment-timezone";
import { contributorEmploymentPageRoute } from "pages/match-main/contributor-employments/ContributorEmploymentPage/ContributorEmploymentPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
// import { matchPath } from "react-router";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const Employments = (props: { initialLoad: boolean; contributorId: number; onGetList: () => void }) => {
  const ROOT_PERMISSION: Permission = Permission.ContributorEmployment;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<ContributorEmploymentListFilter>({
    employmentFilter: { contributorId: props.contributorId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<ContributorEmploymentListDto>("startDate"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<ContributorEmploymentListDto[]>([]);
  const [resource, setResource] = useState<ContributorEmploymentListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.contributorEmployments.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.contributorEmployments.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  return (
    <>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
            <Typography variant="inherit">Employments</Typography>
            <Stack direction="row" justifyContent="flex-end">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate({
                      pathname: contributorEmploymentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                      search: contributorEmploymentPageRoute.getOptionalParams?.({
                        contributorId: props.contributorId,
                      }),
                    })
                  }
                  sx={{ minWidth: "auto" }}
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("partner")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("subPartner")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("startDate")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("endDate")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("hourlySalary")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("groupBenefit")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorEmploymentListDto>("medicalBenefit")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems?.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: contributorEmploymentPageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{listItem.partner}</TableCell>
                    <TableCell>{listItem.subPartner}</TableCell>
                    <TableCell>{formatDate(listItem.startDate)}</TableCell>
                    <TableCell>{formatDate(listItem.endDate)}</TableCell>
                    <TableCell align="right">{formatCurrency(listItem.hourlySalary)}</TableCell>
                    <TableCell align="right">{formatPercent(listItem.groupBenefit)}</TableCell>
                    <TableCell align="right">{formatCurrency(listItem.medicalBenefit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
            props.onGetList();
          },
        }}
      >
        {/* Outlet will only render for the designated route to prevent conflicts with others on parent Page */}
        {matchTemplatePath(contributorEmploymentPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
